/**
 * The prompt bank (design doc §04).
 *
 * These are the voice of the product. Discovery leads with a prompt answer,
 * not a photo, so a weak prompt bank is a weak app — this list should be
 * rewritten by someone who can actually write before launch, and rotated
 * seasonally.
 *
 * Three families:
 *   structure — surfaces how someone handles negotiation, which is the real
 *               compatibility axis for this audience
 *   city      — cheap familiarity, and proof the app was built here rather
 *               than localised into here
 *   boundary  — normalises explicit boundary-setting at profile level; the
 *               safety feature that doesn't look like one
 *
 * REGISTER — read this before editing or translating a single line.
 *
 * This bank is explicit and it is not crude, and the distance between those
 * two things is the entire product. Adults come here to arrange sex with more
 * than one person; a bank that talks only about "connection" and "energy"
 * makes them do the real talking somewhere else, and somewhere else is where
 * people get hurt. So these prompts name desire, bodies, appetite and the hour
 * after — plainly, the way the people answering them already talk.
 *
 * What that never means: anatomical slang, described acts, or anything that
 * reads as an advertisement. The heat here is carried by implication and by
 * specificity — the film nobody watches, the coat you went home in, how close
 * two people had to stand under one awning. A prompt that says the quiet part
 * out loud gets a worse answer than one that leaves the reader to finish it,
 * and the finishing is the flirtation. Write the second kind.
 *
 * Two hard constraints on any edit:
 *   1. §08 moderation is regex-first and stays that way until Content Safety
 *      is wired up. The solicitation and minor-signal rules are literal string
 *      and pattern matches, and a prompt that trips one puts every answer to
 *      it in the review queue. safety.test.ts runs this whole bank through the
 *      live classifier for exactly that reason — keep it green.
 *   2. Nothing here may read as a rate card or a booking. That is the ITPA
 *      line, and it is not a stylistic preference.
 *
 * On top of the families sits an optional SEASON. The header above has always
 * said this bank should be "rotated seasonally", and nothing in the shape said
 * which prompt belonged to which part of the year — so rotation meant editing
 * this file four times a year. A season tag is the whole mechanism: a seasonal
 * prompt is still an ordinary prompt of its family, answerable and pickable in
 * March, but a client that wants to lead with what the city is actually doing
 * this week now has something to sort on. Nothing is hidden out of season; a
 * prompt bank that shrinks in April is a worse bank in April.
 */

import type { Locale } from './domain.js';

export type PromptFamily = 'structure' | 'city' | 'boundary';

/**
 * The Kolkata year, as people here actually divide it. Windows do not overlap,
 * so any date is in exactly one season and `currentPromptSeason` never has to
 * pick a winner.
 */
export const PROMPT_SEASONS = [
  { id: 'boi_mela', en: 'Book Fair', bn: 'বইমেলা', months: [1, 2] },
  { id: 'garam', en: 'The hot months', bn: 'গরমকাল', months: [3, 4, 5] },
  { id: 'borsha', en: 'Monsoon', bn: 'বর্ষা', months: [6, 7, 8, 9] },
  { id: 'pujo', en: 'Pujo', bn: 'পুজো', months: [10, 11] },
  { id: 'boro_din', en: 'Park Street in December', bn: 'বড়দিন', months: [12] },
] as const;

export type PromptSeason = (typeof PROMPT_SEASONS)[number]['id'];

export interface Prompt {
  id: string;
  family: PromptFamily;
  /**
   * Which part of the Kolkata year this one belongs to, if any. Optional
   * because most of the bank is season-less on purpose — "jealousy arrives in
   * my body as…" is not a monsoon question.
   */
  season?: PromptSeason;
  /** The base reading. Always present — every other language falls back to it. */
  en: string;
  /**
   * Bengali. Optional for the same reason Hindi is, and right now missing on
   * every prompt: the bank was rewritten into its current register in English
   * first, and the old Bengali answered the old questions. A reading that asks
   * something the English no longer asks is worse than no reading at all — two
   * people would be answering two different prompts and neither would know.
   * Dropped deliberately, to be restored by the translation pass.
   */
  bn?: string;
  /**
   * Hindi. Optional because the bank is translated as the copy is written, not
   * all at once: §04 is explicit that prompts get written by someone who can
   * actually write, and a machine-translated prompt about veto or appetite will
   * be answered dishonestly. A missing reading falls back to English.
   */
  hi?: string;
  /** Shown when the answer box is empty. Keep it short. */
  hint?: string;
}

/**
 * Which reading of a prompt leads, and which sits underneath it (§06).
 *
 * English readers see English alone. A Hindi or Bengali reader sees their own
 * language leading with the English underneath, because the English is the
 * version their match may have answered against.
 *
 * This replaces the two-language assumption the app shipped with, where the
 * test was `locale === 'bn'` and everything else fell to English-lead with a
 * BENGALI subtitle — which showed Bengali to Hindi speakers.
 */
export function promptReadings(prompt: Prompt, locale: Locale): { lead: string; alt: string | null } {
  const native = locale === 'bn' ? prompt.bn : locale === 'hi' ? prompt.hi : undefined;
  if (!native || native === prompt.en) return { lead: prompt.en, alt: null };
  return { lead: native, alt: prompt.en };
}

export const PROMPTS: Prompt[] = [
  /* ---------------- structure ----------------
   * Negotiation prompts, asked the way the negotiation actually sounds at
   * home. The old bank asked about "agreements" and "opening up" and got back
   * position papers; these ask about appetite, hunger and the hour after, and
   * get back something a stranger can reply to.
   */
  { id: 's01', family: 'structure', en: 'The appetite we are still negotiating is…' },
  { id: 's02', family: 'structure', en: 'What I got wrong the first time there was somebody else in the bed' },
  { id: 's03', family: 'structure', en: 'Whether I say what I want on the first night or the fifth' },
  { id: 's04', family: 'structure', en: 'Jealousy arrives in my body as…' },
  { id: 's05', family: 'structure', en: 'When one of us is wanted more than the other that month, we…' },
  { id: 's06', family: 'structure', en: 'What nobody warns you about wanting two people at once' },
  { id: 's07', family: 'structure', en: 'Undressed of the polite version, what I am looking for is…' },
  { id: 's08', family: 'structure', en: 'My partner and I want different amounts of…' },
  { id: 's09', family: 'structure', en: 'How much I want to be told about your nights with them' },
  { id: 's10', family: 'structure', en: 'The calendar is the hardest part, because the wanting is not' },
  { id: 's11', family: 'structure', en: 'What "slow" means in my mouth' },
  { id: 's12', family: 'structure', en: 'What I was hungry for before I had a word for it' },
  { id: 's13', family: 'structure', en: 'The sentence I have finally learned to say out loud, in the dark' },
  { id: 's14', family: 'structure', en: 'The version of this that would leave me cold' },
  { id: 's15', family: 'structure', en: 'What my partner would warn you about me, fondly' },

  /* ---------------- city ----------------
   * The double meaning lives here. A city prompt is still small talk — it has
   * to be, it is the cheap one — but every one of these can be answered
   * innocently or not, and which way somebody takes it is the information.
   */
  { id: 'c01', family: 'city', en: 'Where I am still talking at three in the morning, and what it usually turns into' },
  { id: 'c02', family: 'city', en: 'The Pujo I skip, and whose room I am in instead' },
  { id: 'c03', family: 'city', en: 'College Street or Park Street — and which one I take someone to when I mean it' },
  { id: 'c04', family: 'city', en: 'The most overrated thing in Kolkata, and what I would rather do with the evening' },
  { id: 'c05', family: 'city', en: 'Where I go in this city when I want nobody to touch me' },
  { id: 'c06', family: 'city', en: 'A first meeting that is not a coffee shop and ends somewhere warmer' },
  { id: 'c07', family: 'city', en: 'The last bus I let go past on purpose' },
  { id: 'c08', family: 'city', en: 'My order at the para roll shop, and the hour I am usually ordering it' },
  { id: 'c09', family: 'city', en: 'Book fair, film festival, or the flat with the door shut' },
  { id: 'c10', family: 'city', en: 'Rain in this city is either an excuse or an invitation. Mine is…' },
  { id: 'c11', family: 'city', en: 'What I defend about Kolkata to people this city has never kept awake' },
  { id: 'c12', family: 'city', en: 'Where I actually live, and whether you are coming up' },
  { id: 'c13', family: 'city', en: 'A Bengali film I would put on and then not watch' },
  { id: 'c14', family: 'city', en: 'My unpopular opinion about what Pujo nights are really for' },
  { id: 'c15', family: 'city', en: 'Sunday morning at my place looks like…' },

  /* ------- city, seasonal -------
   * Still city prompts — same family, same chip, same ninety seconds to
   * answer. The season tag only says when they land hardest.
   *
   * Every one of these asks for a memory rather than a position. "My
   * unpopular opinion about what Pujo nights are really for" (c14) gets you an
   * argument, which is fine; "where were you when the dhak started" gets you a
   * rooftop, a staircase, a hand held where somebody might have seen. The
   * second is the one you can reply to, and replying is the only thing this
   * app charges for.
   */
  { id: 'c16', family: 'city', season: 'pujo', en: 'Where I actually was when the dhak started this year, and who I had slipped away with' },
  { id: 'c17', family: 'city', season: 'pujo', en: 'What Bijoya looks like in my house once the guests have gone' },
  { id: 'c18', family: 'city', season: 'borsha', en: 'The last time I got soaked and took my time getting dry' },
  { id: 'c19', family: 'city', season: 'borsha', en: 'Where I waited out a downpour, and how close we had to stand' },
  { id: 'c20', family: 'city', season: 'boi_mela', en: 'What I carried home from the last Boi Mela, and who carried it up the stairs' },
  { id: 'c21', family: 'city', season: 'boi_mela', en: 'A Boi Mela evening where neither of us bought a book' },
  { id: 'c22', family: 'city', season: 'garam', en: 'What my house does when the lights go out and nobody can sleep' },
  { id: 'c23', family: 'city', season: 'garam', en: 'The best hour I have spent by candlelight, waiting for the power' },
  { id: 'c24', family: 'city', season: 'boro_din', en: 'Park Street in December — the year I did not go home afterwards' },
  { id: 'c25', family: 'city', season: 'boro_din', en: 'Where I end up on the twenty-fifth, and whose coat I am wearing' },

  /* ---------------- boundary ----------------
   * The safety feature that does not look like one, and the family where being
   * explicit does the most work: somebody who has already written down what
   * they want asked for is much harder to talk past. Consent language, in the
   * first person, at profile level, before anyone has typed hello.
   */
  { id: 'b01', family: 'boundary', en: 'A hard no, and I am not going to explain it' },
  { id: 'b02', family: 'boundary', en: 'How I want to be told the wanting has stopped' },
  { id: 'b03', family: 'boundary', en: 'How much warning I need before you change your mind about an evening' },
  { id: 'b04', family: 'boundary', en: 'Text me like this when it is late, not like that' },
  { id: 'b05', family: 'boundary', en: 'What discretion means to me, specifically, down to the street' },
  { id: 'b06', family: 'boundary', en: 'Do not ask me for…' },
  { id: 'b07', family: 'boundary', en: 'I go quiet afterwards. It means…' },
  { id: 'b08', family: 'boundary', en: 'Before you touch me, I would like…' },
  { id: 'b09', family: 'boundary', en: 'My pace, said plainly, including the part people find slow' },
  { id: 'b10', family: 'boundary', en: 'The fastest way to cool me down' },
  { id: 'b11', family: 'boundary', en: 'I am very good at saying no to…' },
  { id: 'b12', family: 'boundary', en: 'What I need in the hour after, which is not what I need during' },
  { id: 'b13', family: 'boundary', en: 'How I feel about being wanted only behind a closed door' },
  { id: 'b14', family: 'boundary', en: 'In public, your hands are welcome to…' },
  { id: 'b15', family: 'boundary', en: 'What I will always tell you, even with the lights on' },
];

export const PROMPTS_BY_ID: Record<string, Prompt> = Object.fromEntries(
  PROMPTS.map((p) => [p.id, p]),
);

/** Minimum answered prompts before discovery unlocks (§06, onboarding step 6). */
export const MIN_PROMPT_ANSWERS = 2;
export const MAX_PROMPT_ANSWERS = 3;
export const PROMPT_ANSWER_MIN_CHARS = 12;
export const PROMPT_ANSWER_MAX_CHARS = 280;

export function promptsForFamily(family: PromptFamily): Prompt[] {
  return PROMPTS.filter((p) => p.family === family);
}

export function promptsForSeason(season: PromptSeason): Prompt[] {
  return PROMPTS.filter((p) => p.season === season);
}

/**
 * Which season it is in Kolkata right now.
 *
 * IST, not the server's clock: at 23:00 in Kolkata on the last day of Ashwin a
 * UTC server is still in the previous month, and the one week of the year this
 * matters most is exactly the week it would get wrong.
 */
export function currentPromptSeason(now: Date = new Date()): PromptSeason {
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  const month = ist.getUTCMonth() + 1;
  const season = PROMPT_SEASONS.find((s) => (s.months as readonly number[]).includes(month));
  // The windows partition the year, so this fallback is unreachable — but a
  // prompt picker that throws in some month is worse than one that says pujo.
  return season?.id ?? 'pujo';
}
